import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Loader2, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";

export default function Premium() {
  const { t, lang } = useLang();
  const { user, login } = useAuth();
  const [plans, setPlans] = useState([]);
  const [perks, setPerks] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/premium/plans").then((r) => {
      setPlans(r.data.plans || []);
      setPerks((r.data.perks && (r.data.perks[lang] || r.data.perks.it)) || []);
    }).catch(() => {});
    if (user) api.get("/me/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [lang, user]);

  const subscribe = async (plan) => {
    if (!user) { login(); return; }
    setBusy(plan.lookup_key);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key, origin_url: window.location.origin,
        item_type: "premium", item_id: plan.lookup_key,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Errore checkout");
      setBusy(null);
    }
  };

  const cancel = async () => {
    try {
      await api.post("/me/subscription/cancel");
      toast.success("Cancellazione programmata a fine periodo");
      const r = await api.get("/me/subscription");
      setSub(r.data);
    } catch { toast.error("Errore"); }
  };

  const active = sub?.active;

  return (
    <div data-testid="premium-page">
      <PageHeader eyebrow="Premium" title={t("premium_title")} desc={t("premium_desc")} />

      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        {active && (
          <div className="mb-10 rounded-2xl border border-[rgba(198,137,158,0.4)] bg-[rgba(198,137,158,0.08)] p-6 flex items-center justify-between flex-wrap gap-4" data-testid="subscription-active">
            <div className="flex items-center gap-3">
              <BadgeCheck className="w-6 h-6 text-[#C6899E]" />
              <div>
                <div className="font-serif text-xl">Premium attivo</div>
                <div className="text-sm text-[#8C7E83]">{sub?.cancel_at_period_end ? "Si concluderà a fine periodo." : "Grazie per il tuo supporto."}</div>
              </div>
            </div>
            {!sub?.cancel_at_period_end && (
              <button onClick={cancel} data-testid="cancel-subscription"
                className="text-xs uppercase tracking-[0.15em] text-[#9C8F94] hover:text-[#3D3238] border border-[rgba(61,50,56,0.15)] rounded-full px-4 py-2">
                {lang === "it" ? "Annulla" : "Cancel"}
              </button>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          {plans.map((plan, i) => (
            <motion.div key={plan.lookup_key}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`rounded-3xl border p-8 sm:p-10 ${plan.interval === "year" ? "border-[rgba(198,137,158,0.5)] bg-[#FFFFFF]" : "border-[rgba(61,50,56,0.1)] bg-[#FFFFFF]"}`}
              data-testid={`plan-${plan.lookup_key}`}>
              {plan.interval === "year" && (
                <div className="inline-flex items-center gap-1.5 mb-4 text-[0.65rem] uppercase tracking-[0.15em] text-[#C6899E] border border-[rgba(198,137,158,0.3)] rounded-full px-3 py-1">
                  <Sparkles className="w-3 h-3" /> {lang === "it" ? "Miglior valore" : "Best value"}
                </div>
              )}
              <div className="font-serif text-2xl">{plan.name}</div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-serif text-5xl">€{plan.price}</span>
                <span className="text-[#9C8F94] mb-2 text-sm">{plan.interval === "year" ? t("per_year") : t("per_month")}</span>
              </div>
              <button onClick={() => subscribe(plan)} disabled={busy === plan.lookup_key || active}
                data-testid={`subscribe-${plan.lookup_key}`}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors disabled:opacity-40">
                {busy === plan.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : t("subscribe")}
              </button>
            </motion.div>
          ))}
        </div>

        {perks.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {perks.map((p) => (
              <div key={p} className="flex items-center gap-3 text-[#8C7E83]">
                <span className="w-6 h-6 rounded-full bg-[rgba(198,137,158,0.15)] flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#C6899E]" />
                </span>
                {p}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
